import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import PasswordField from 'components/PasswordField';
import { useTranslation } from 'react-i18next';
import ButtonLoading from 'components/ButtonLoading';
import { toast } from 'react-toastify';
import Notification from 'components/Notification';
import { NOTIFICATION_TYPES } from 'utils/constants/notifications';
import { patchPasswordChange, postResetPwd } from 'utils/network/http';
import { useAuth } from 'hooks/useAuth/useAuth';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'utils/constants/routes';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';

const PasswordReset = ({ className, isUserLogged, recoverToken }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [oldPasswordError, setOldPasswordError] = useState(false);
  const [invalidTokenError, setInvalidTokenError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('AccountSettings');
  const { profile } = useAuth();
  const history = useHistory();

  const checkNewPassword = useCallback(() => {
    return (
      newPassword === confirmPassword &&
      newPassword.length >= 4 &&
      newPassword.length <= 8
    );
  }, [newPassword, confirmPassword]);

  const showSuccessToast = useCallback(() => {
    toast(
      <Notification
        title={t('passwordSuccessTitle')}
        type={NOTIFICATION_TYPES.SUCCESS}
        content={t('passwordSuccess')}
      />,
      { autoClose: false, position: toast.POSITION.TOP_LEFT }
    );
    if (isUserLogged) {
      history.push(ROUTES.OFFERS);
    } else {
      history.push(ROUTES.LOGIN);
    }
  }, [t, history, isUserLogged]);

  const handlePasswordChangeFromAccount = useCallback(
    async (user_id, user_password, current_password) => {
      try {
        setLoading(true);
        setOldPasswordError(false);
        await patchPasswordChange(user_id, user_password, current_password);
        showSuccessToast();
        setNewPassword('');
        setConfirmPassword('');
        setOldPassword('');
        sendEventToSegment(TRACKING_EVENTS.CHANGE_PASSWORD);
      } catch (e) {
        console.error('Profile password change.', e);
        setOldPasswordError(true);
      } finally {
        setLoading(false);
      }
    },
    [showSuccessToast]
  );

  const handlePasswordChangeFromRecover = useCallback(async () => {
    try {
      setLoading(true);
      setInvalidTokenError(false);
      await postResetPwd(recoverToken, newPassword);
      sendEventToSegment(TRACKING_EVENTS.RESET_PASSWORD);
      showSuccessToast();
    } catch (err) {
      console.error('There was an error fetching postResetPwd', err);
      setInvalidTokenError(true);
    } finally {
      setLoading(false);
    }
  }, [recoverToken, newPassword, showSuccessToast]);

  const handleSubmit = useCallback(() => {
    setPasswordError(false);
    if (!checkNewPassword()) {
      setPasswordError(true);
    } else {
      if (isUserLogged) {
        handlePasswordChangeFromAccount(
          profile?.user?.id,
          newPassword,
          oldPassword
        );
      } else {
        handlePasswordChangeFromRecover();
      }
    }
  }, [
    profile,
    newPassword,
    oldPassword,
    checkNewPassword,
    handlePasswordChangeFromAccount,
    isUserLogged,
    handlePasswordChangeFromRecover
  ]);

  return (
    <div className={className} data-testid="password-reset">
      <div className="password-reset">
        {profile?.user?.is_pwd_set_by_user && isUserLogged && (
          <PasswordField
            id="password_old"
            data-testid="password-reset__password-old"
            label={t('oldPassword')}
            inputClass={cx('password-reset__input', {
              'password-reset__input--error': oldPasswordError
            })}
            value={oldPassword}
            onChange={(e, { value }) => setOldPassword(value)}
            error={oldPasswordError}
          />
        )}
        {profile?.user?.is_pwd_set_by_user && isUserLogged && oldPasswordError && (
          <div
            className="password-reset__error"
            data-testid="password-reset__old-error"
          >
            {t('oldPasswordError')}
          </div>
        )}
        <PasswordField
          id="password_new"
          label={t('newPassword')}
          inputClass="password-reset__input"
          value={newPassword}
          error={passwordError}
          onChange={(e, { value }) => setNewPassword(value)}
        />
        <PasswordField
          id="password_confirm"
          label={t('confirmPassword')}
          name="password_confirm"
          inputClass={cx('password-reset__input', {
            'password-reset__input--error': passwordError || invalidTokenError
          })}
          error={passwordError}
          value={confirmPassword}
          onChange={(e, { value }) => setConfirmPassword(value)}
        />
        {passwordError && (
          <div
            className="password-reset__error"
            data-testid="password-reset__match-error"
          >
            {t('passwordError')}
          </div>
        )}
        {invalidTokenError && (
          <div
            className="password-reset__error"
            data-testid="password-reset__token-error"
          >
            {t('tokenError')}
          </div>
        )}
        <ButtonLoading
          loading={loading}
          handleClick={handleSubmit}
          extraClass="password-reset__submit"
          text={t('savePassword')}
        />
      </div>
    </div>
  );
};

PasswordReset.defaultProps = {
  isUserLogged: false,
  recoverToken: ''
};

PasswordReset.propTypes = {
  className: PropTypes.string,
  isUserLogged: PropTypes.bool,
  recoverToken: PropTypes.string
};
export default PasswordReset;
